import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ShieldCheck, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminClaimPage() {
  const nav = useNavigate();
  const { setAuthFromToken } = useAuth();
  const [checking, setChecking] = useState(true);
  const [available, setAvailable] = useState(null);
  const [reason, setReason] = useState(null);
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/claim-admin-available`);
        setAvailable(r.data.available);
        if (!r.data.available) setReason(r.data.reason || 'unavailable');
      } catch (e) {
        setErr('Could not reach the server. Try again in a moment.');
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (pw.length < 12) {
      setErr('Password must be at least 12 characters.');
      return;
    }
    if (!/[A-Za-z]/.test(pw) || !/[0-9]/.test(pw)) {
      setErr('Password must contain at least one letter and one digit.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/claim-admin`, {
        email: email.trim().toLowerCase(),
        password: pw,
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
      });
      setAuthFromToken(r.data.token, r.data.user);
      setOk(true);
      setTimeout(() => nav('/admin/dashboard', { replace: true }), 1200);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === 'string' ? detail : 'Claim failed. Try again.');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50 dark:from-slate-950 dark:to-slate-900 flex items-center justify-center p-4"
      data-testid="admin-claim-page"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-violet-600 text-white mb-3">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Claim Admin
          </h1>
          <p className="text-sm text-cn-text-muted mt-2">
            One-time setup for the first administrator on this deployment.
          </p>
        </div>

        {checking && (
          <div className="cn-card p-8 text-center text-cn-text-muted">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Checking availability…
          </div>
        )}

        {!checking && available === false && (
          <div className="cn-card p-6" data-testid="claim-unavailable">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="font-semibold text-sm mb-1">
                  Admin already claimed
                </h2>
                <p className="text-sm text-cn-text-muted">
                  An administrator account already exists on this
                  deployment. This one-time claim link is permanently
                  disabled. Contact your existing admin, or Emergent
                  Support if you can no longer reach them.
                </p>
                <a
                  href="/auth"
                  className="inline-block mt-4 text-sm text-violet-700 hover:underline"
                >
                  Go to sign-in →
                </a>
              </div>
            </div>
          </div>
        )}

        {!checking && available === true && !ok && (
          <form onSubmit={submit} className="cn-card p-6 space-y-4" data-testid="claim-form">
            <div>
              <label className="block text-xs uppercase tracking-wider text-cn-text-muted mb-1">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-cn-line rounded px-3 py-2 bg-white dark:bg-slate-900 text-sm"
                data-testid="claim-email"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-cn-text-muted mb-1">
                Password (min 12 chars, letter + digit)
              </label>
              <input
                type="password"
                required
                minLength={12}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="w-full border border-cn-line rounded px-3 py-2 bg-white dark:bg-slate-900 text-sm font-mono"
                data-testid="claim-password"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs uppercase tracking-wider text-cn-text-muted mb-1">
                  First name (optional)
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full border border-cn-line rounded px-3 py-2 bg-white dark:bg-slate-900 text-sm"
                  data-testid="claim-first-name"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-cn-text-muted mb-1">
                  Last name (optional)
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full border border-cn-line rounded px-3 py-2 bg-white dark:bg-slate-900 text-sm"
                  data-testid="claim-last-name"
                />
              </div>
            </div>

            {err && (
              <div
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
                data-testid="claim-error"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !email || !pw}
              className="w-full cn-btn cn-btn-primary text-sm py-2.5 disabled:opacity-50"
              data-testid="claim-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Claiming…
                </>
              ) : (
                'Claim admin'
              )}
            </button>

            <p className="text-xs text-cn-text-muted">
              This link works only once. After you claim, it&apos;s
              permanently disabled. You can rotate your password
              anytime from account settings.
            </p>
          </form>
        )}

        {ok && (
          <div className="cn-card p-6 text-center" data-testid="claim-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
            <h2 className="font-semibold text-sm mb-1">
              Admin created &mdash; you&apos;re signed in
            </h2>
            <p className="text-sm text-cn-text-muted">
              Redirecting to the admin dashboard…
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
